import { FC, useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { PerCardInfo } from '../../../../components/custom_components/CardInfo'
import PerFlexRow from '../../../../components/custom_components/FlexRow'
import MeetingBackground from '../../../../components/custom_components/Meeting/MeetingBackground'
import PerSpacer from '../../../../components/custom_components/Spacer'
import PerText from '../../../../components/custom_components/Text'
import { employeeConfirmPreparation } from '../../../../graphql/appraisal-flow/hooks/useEmployeeConfirmPreparation'
import { FullMeetingFragment, MeetingStep, MeetingStepStatus, MeetingStepViewId } from '../../../../types'
import { AppraisalStepBtns } from '../shared/appraisal-types'
import { AppraisalFlowHelper } from '../shared/AppraisalFlowHelper'
import { AppraisalFlowContext } from '../shared/AppraisalFlowProvider'
import { addOrUpdateFeedback } from '../shared/AppraisalMeetingService'
import AppraisalPageDescription from '../shared/AppraisalPageDescription'
import { MeetingConfirmBtn } from '../shared/MeetingStepButton'
import { EmployeeMeetingModalAppraisal, MeetingModalEmployeePreparation } from '../shared/modals/MeetingModalEmployeePreparation'
import { generateStepName, getIncompleteStepReason } from '../shared/util'
import { showNotification } from '../../../../components/custom_components/Notification/notification'
import RichTextBox from '../../../../components/custom_components/RichTextBox'

interface EmployeeMeetingStepFeedbackProps {
  meeting: FullMeetingFragment
}
const EmployeeMeetingStepFeedback: FC<EmployeeMeetingStepFeedbackProps & AppraisalStepBtns> = ({ meeting, backBtn }) => {
  const [t] = useTranslation()
  const history = useHistory()
  const { meetingTypeSteps } = useContext(AppraisalFlowContext)
  const [open, changeOpen] = useState(false)
  const [valid, setValid] = useState(true)
  const [feedbackState, setFeedbackState] = useState<string | undefined>(
    meeting.appraisal && meeting.appraisal.employeeGeneralFeedback ? meeting.appraisal.employeeGeneralFeedback : undefined,
  )

  const updateFeedback = async () => {
    if (feedbackState)
      try {
        await addOrUpdateFeedback(meeting.id, feedbackState)
        setValid(true)
      } catch (err) {
        showNotification({
          type: 'error',
          message: t(`appraisalMeeting.stepFeedback.notifications.error.title`),
          description: t(`appraisalMeeting.stepFeedback.notifications.error.description`),
        })
        setValid(false)
      }
  }

  const closePreparationAndRedirect = async (selfReflectionShared: boolean, evaluationShared: boolean) => {
    try {
      await employeeConfirmPreparation(meeting.id, {
        selfReflectionShared: selfReflectionShared,
        evaluationShared: evaluationShared,
      })
      setValid(true)
      history.goBack()
    } catch (err) {
      showNotification({
        type: 'error',
        message: t(`appraisalMeeting.stepFeedback.notifications.error.title`),
        description: t(`appraisalMeeting.stepFeedback.notifications.error.description`),
      })
      setValid(false)
    }
  }

  const incompleteMeetingSteps: MeetingStep[] = meetingTypeSteps
    .map((step, index) => ({ ...step, position: index + 1 }))
    .filter((step) => step.viewId !== MeetingStepViewId.Feedback && step.status !== MeetingStepStatus.Done)

  const renderReason = ({ id, position, viewId, status }: MeetingStep) => (
    <li key={id}>
      <PerText mt="xxxxs">
        {t('appraisalMeeting.stepFeedback.infoCard.incompleteStep', {
          position: position,
          stepName: generateStepName(viewId, meeting.type),
          reason: getIncompleteStepReason(status),
        })}
      </PerText>
    </li>
  )

  return (
    <>
      <MeetingModalEmployeePreparation
        onCancel={() => changeOpen(false)}
        onConfirm={closePreparationAndRedirect}
        visible={open && valid}
        appraisal={meeting.appraisal as EmployeeMeetingModalAppraisal}
        hasSelfreflection={meeting.meetingSteps ? meeting.meetingSteps.some((s) => s.viewId === MeetingStepViewId.SelfReflection) : false}
        hasEvaluation={meeting.meetingSteps ? meeting.meetingSteps.some((s) => s.viewId === MeetingStepViewId.Evaluation) : false}
      />

      <MeetingBackground withFooter={false}>
        <PerText appearance="subtitle" color="primary" mb="m">
          {t('appraisalMeeting.stepFeedback.heading')}
        </PerText>
        {incompleteMeetingSteps.length > 0 && (
          <>
            <PerCardInfo
              heading={t('appraisalMeeting.stepFeedback.infoCard.heading')}
              description={t('appraisalMeeting.stepFeedback.infoCard.description')}
              childrenMarginTop="xxxxs"
              icon={{ name: 'warning_fill' }}
            >
              <PerText appearance="info" ml="xxxl">
                <ul>{incompleteMeetingSteps.map(renderReason)}</ul>
              </PerText>
            </PerCardInfo>
            <PerSpacer mb="xl" />
          </>
        )}
        <AppraisalPageDescription description={t('appraisalMeeting.stepFeedback.pageDescriptionPreparation')} />
        <RichTextBox
          data-e2e-feedback-textarea
          placeholder={t('appraisalMeeting.stepFeedback.placeholder')}
          onBlur={updateFeedback}
          text={feedbackState || ''}
          onChange={(text) => {
            setFeedbackState(text)
          }}
        />
      </MeetingBackground>
      <PerFlexRow justify="space-between">
        {backBtn && backBtn()}
        <MeetingConfirmBtn
          onClick={async () => {
            // Manually trigger feedback save because onBlur is not reliably fired
            // when the user clicks the button while the RichTextBox is still focused.
            await updateFeedback()
            changeOpen(true)
          }}
        />
      </PerFlexRow>
      <AppraisalFlowHelper stepViewId={MeetingStepViewId.Feedback} stepStatus={MeetingStepStatus.Visited} />
    </>
  )
}
export default EmployeeMeetingStepFeedback
